import { compareAsc } from 'date-fns';
import { defineStore } from 'pinia';

import { MilestoneEntity, OrderEntity } from '@/common/types/entities';
import { useMilestoneStore } from '@/core/milestones/milestoneStore';
import { useCapacityStore } from '@/features/capacities/capacityStore';
import { WorkerThresholdExceededAlert } from '@/features/capacities/types';
import { useCollisionStore } from '@/features/collisions/collisionStore';
import { Collision } from '@/features/collisions/types';
import { useOrderStore } from '@/features/orders/orderStore';
import { NodeName } from '@/repositories/utils/cache';

import { ScheduleAlert } from './types';

type GroupedScheduleAlerts = {
  milestones: [string, Collision[], MilestoneEntity][];
  orders: [string, Collision[], OrderEntity][];
  thresholds: [string, WorkerThresholdExceededAlert[], Date][];
};

function getCollisionsByTarget(
  collisions: Map<string, Collision> | Collision[],
): Map<string, Collision[]> {
  const collisionsMap = new Map<string, Collision[]>();
  collisions.forEach((collision) => {
    const existing = collisionsMap.get(collision.toId);
    if (existing) {
      existing.push(collision);
    } else {
      collisionsMap.set(collision.toId, [collision]);
    }
  });
  return collisionsMap;
}

export const useScheduleAlertStore = defineStore('schedule-alert-store', () => {
  const collisionStore = useCollisionStore();
  const capacityStore = useCapacityStore();
  const milestoneStore = useMilestoneStore();
  const orderStore = useOrderStore();
  const scheduleAlerts = ref<Map<string, ScheduleAlert>>(new Map());

  watch(
    [() => collisionStore.collisionsList, () => capacityStore.workerThresholdExceededAlertList],
    ([collisionsList, workerThresholdExceededAlertList]) => {
      const newAlertsMap = new Map<string, Collision | WorkerThresholdExceededAlert>();

      collisionsList.forEach((collision) => {
        newAlertsMap.set(collision.dependencyId, collision);
      });

      workerThresholdExceededAlertList.forEach((alert) => {
        newAlertsMap.set(alert.id, alert);
      });

      scheduleAlerts.value = newAlertsMap;
    },
    { immediate: true },
  );

  const scheduleAlertList = computed(() => {
    return Array.from(scheduleAlerts.value.values());
  });

  const groupedScheduleAlerts = computed(() => {
    const groups: GroupedScheduleAlerts = {
      milestones: [],
      orders: [],
      thresholds: [],
    };
    const collisionsGroupedByTarget = getCollisionsByTarget(collisionStore.collisions);

    collisionsGroupedByTarget.forEach((collisions, targetId) => {
      if (!collisions.length) return;
      if (collisions[0].toType === NodeName.MILESTONE) {
        const milestone = milestoneStore.milestones.get(targetId);
        if (!milestone) return;
        groups.milestones.push([targetId, collisions, milestone]);
      } else if (collisions[0].toType === NodeName.ORDER) {
        const order = orderStore.orders.get(targetId);
        if (!order) return;
        groups.orders.push([targetId, collisions, order]);
      }
    });

    // Group threshold alerts by date
    const thresholdAlertsGroupedByDate = new Map<string, WorkerThresholdExceededAlert[]>();
    capacityStore.workerThresholdExceededAlertList.forEach((alert) => {
      const dateKey = alert.date.toISOString();
      const existing = thresholdAlertsGroupedByDate.get(dateKey);
      if (existing) {
        existing.push(alert);
      } else {
        thresholdAlertsGroupedByDate.set(dateKey, [alert]);
      }
    });

    thresholdAlertsGroupedByDate.forEach((alerts, dateKey) => {
      groups.thresholds.push([dateKey, alerts, new Date(dateKey)]);
    });

    groups.milestones.sort((a, b) => compareAsc(a[2].date, b[2].date));
    groups.orders.sort((a, b) => compareAsc(a[2].startAt, b[2].startAt));
    groups.thresholds.sort((a, b) => compareAsc(a[2], b[2]));

    return groups;
  });

  return {
    scheduleAlerts,
    scheduleAlertList,
    groupedScheduleAlerts,
  };
});

export type ScheduleAlertStore = ReturnType<typeof useScheduleAlertStore>;
